import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactMarkdown from 'react-markdown';

import {trackEvent} from 'analytics/Analytics';
import {DEEP_RESEARCH_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {DeepResearchMessageType} from 'llm/deep-research/types';
import LLMMarkdownSection, {createBenchlingTagConfigs} from 'llm/markdown/LLMMarkdownSection';
import {cleanMarkdownForSaving} from 'llm/markdown/LLMMarkdownUtils';
import {remarkGfmTables} from 'llm/markdown/remarkTables';
import {showCreateEntryModal} from 'nav/CreateDropdownActions';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {WYCIWYPPart} from 'notebook/mediocre/clipboard/WyciwypConstants';
import {makeItemFromWyciwypPart} from 'notebook/notes/CreateOps';
import {InitialNotePart, NotePartType} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export function convertMarkdownToNoteParts(markdownContent: string): Array<InitialNotePart> {
  // Clean the markdown content by removing tags that shouldn't be kept when saving
  const tagConfigs = createBenchlingTagConfigs({});
  const cleanedMarkdown = cleanMarkdownForSaving(markdownContent, tagConfigs);

  // Convert markdown to HTML using a simple converter for basic markdown
  const htmlContent = ReactDOMServer.renderToStaticMarkup(
    <ReactMarkdown remarkPlugins={[remarkGfmTables]}>{cleanedMarkdown}</ReactMarkdown>
  );

  // Convert markdown to noteparts using the existing wyciwyp system
  const mockClipboardData = {
    types: ['text/html', 'text/plain'],
    getData: (type: string) => {
      if (type === 'text/html') {
        return htmlContent;
      }
      if (type === 'text/plain') {
        return cleanedMarkdown;
      }
      return '';
    },
  } as unknown as DataTransfer;

  const wyciwypParts = wyciwyp.processClipboard(mockClipboardData, {
    allowTables: true,
    allowHeaders: false, // Headers are not supported in makeItemFromWyciwypPart; this makes them bold instead
    allowLinks: false,
  });

  // Convert wyciwyp parts to proper initial note parts using the existing utility
  const usedTableNames: Array<string> = [];
  const noteparts = wyciwypParts.map((part: WYCIWYPPart) => {
    const {item} = makeItemFromWyciwypPart(part, usedTableNames, true);
    const intialNotePart: InitialNotePart =
      item.type === NotePartType.TABLE
        ? {
            type: NotePartType.TABLE,
            table: item.table!,
          }
        : {
            type: NotePartType.TEXT,
            text: item.text,
            indentation: item.indentation,
            listType: item.listType,
            formats: item.formats!,
          };
    return intialNotePart;
  });

  return noteparts;
}

export interface ReportDeepResearchMessage {
  type: DeepResearchMessageType.SYSTEM_REPORT;
  content: string;
}

export interface ReportMessageProps {
  message: ReportDeepResearchMessage;
  onCitationClick: (toolId: string) => void;
}

export function ReportMessage({message, onCitationClick}: ReportMessageProps): JSX.Element {
  const handleSaveClick = (): void => {
    // When we get a context with a chat ID, we can add it here to the metadata
    const noteParts = convertMarkdownToNoteParts(message.content);
    trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY);
    showCreateEntryModal({
      initialFolderId: undefined,
      initialNoteParts: noteParts,
      onEntryCreated: () => {
        notifications.success('Entry created and research report content has been automatically added!');
      },
    });
  };

  return (
    <div className={css(styles.systemReportLayout)} data-testid="system-report-message">
      <div className={css(styles.systemReportContainer)}>
        <div className={css(styles.systemReportContent)}>
          <LLMMarkdownSection markdown={message.content} onCitationClick={onCitationClick} />
        </div>
        <div className={css(styles.systemReportToolbar)}>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            onClick={handleSaveClick}
            icon={<BenchlingIcon icon="save" />}
            text="Save to Entry"
          />
        </div>
      </div>
      <div className={css(styles.systemReportFooter)} data-testid="system-report-footer">
        Reports are not auto saved during beta
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  // System report styles
  systemReportLayout: {
    display: 'flex',
    flexDirection: 'column',
  },

  systemReportContainer: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
    padding: '12px',
  },

  systemReportContent: {
    ...FONT_PROXIMA_14,
  },

  systemReportToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  systemReportFooter: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
    fontSize: '12px',
    padding: '4px 12px',
    textAlign: 'center',
  },
});
